import { createSelector } from 'reselect';

import { RootModel } from '../models';
import {
  ContentItemTypes,
  LocationGroupTypes,
  LocationItemDetailTypes,
  MapLocationTypes,
} from '../../types';
import { getLocationDistanceInfo, getLocationName, getLocationOriginalData } from '../../utils';

export const getSelectedGroupItems = createSelector<
  RootModel,
  { [key: number]: LocationGroupTypes },
  { [key: number]: MapLocationTypes },
  { key: string; variable: string },
  number,
  number,
  ContentItemTypes[]
>(
  (state) => state.locationGroupEntity.ids,
  (state) => state.mapLocationEntity.ids,
  (state) => state.locationModel.transport,
  (state) => state.locationModel.groupId,
  (state) => state.locationModel.subGroupId,
  (locationGroupIds, mapLocationEntityIds, transport, groupId, subGroupId) => {
    const group = locationGroupIds[subGroupId] || locationGroupIds[groupId];
    if (!group) return [];

    const ids = group.itemIds;
    const items: ContentItemTypes[] = [];
    ids.forEach((id) => {
      const item = mapLocationEntityIds[id];
      if (!item) return;

      let subTitle: string;
      if (item.distanceMatrix && item.distanceMatrix[transport.variable]) {
        const result = getLocationDistanceInfo(item.distanceMatrix[transport.variable]);
        if (result) subTitle = result.distance;
      }

      const isCounting = !!group.direction;

      items.push({
        id: item.id,
        title: getLocationName(item),
        subTitle,
        avatar: item.mapLocationInfo ? item.mapLocationInfo.avatar : undefined,
        icon: item.mapLocationInfo ? item.mapLocationInfo.icon : undefined,
        iconBgColor: item.mapLocationInfo ? item.mapLocationInfo.iconBgColor : undefined,
        isCounting,
        url360: item.mapLocationInfo ? item.mapLocationInfo.virtualTourUrl : undefined,
      });
    });

    return items;
  }
);

export const getSelectedGroupLocations = createSelector<
  RootModel,
  { [key: number]: LocationGroupTypes },
  { [key: number]: MapLocationTypes },
  number[],
  number,
  number,
  number,
  MapLocationTypes[]
>(
  (state) => state.locationGroupEntity.ids,
  (state) => state.mapLocationEntity.ids,
  (state) => state.projectModel.locationGroupsIds,
  (state) => state.locationModel.groupId,
  (state) => state.locationModel.subGroupId,
  (state) => state.locationModel.numberOfDisplayItem,
  (
    locationGroupIds,
    mapLocationEntityIds,
    locationGroupsIds,
    groupId,
    subGroupId,
    numberOfDisplayItem
  ) => {
    const selectedGroup = locationGroupIds[subGroupId] || locationGroupIds[groupId];
    if (!selectedGroup) {
      if (!locationGroupsIds) return [];
      // case show all locations on map
      const parentGroups: LocationGroupTypes[] = [];
      let childGroupIds: number[] = [];

      locationGroupsIds.forEach((id) => {
        const item = locationGroupIds[id];
        // check item is parent group
        if (item && childGroupIds.indexOf(item.id) === -1) {
          parentGroups.push(item);
          // push child group ids for check with next array
          if (item.childGroupIds && !!item.childGroupIds.length) {
            childGroupIds = [...childGroupIds, ...item.childGroupIds];
          }
        }
      });

      // get location and check duplicate id
      const items: MapLocationTypes[] = [];
      const itemLocationIds: number[] = [];
      if (parentGroups.length) {
        parentGroups.forEach((group) => {
          if (group && group.itemIds && group.itemIds.length) {
            group.itemIds.slice(0, numberOfDisplayItem).forEach((id) => {
              const item = mapLocationEntityIds[id];

              if (
                item &&
                item.mapLocationInfo &&
                itemLocationIds.findIndex((i) => i === item.mapLocationInfo.id) === -1
              ) {
                items.push({ ...item, parentKey: group.id });
                itemLocationIds.push(item.mapLocationInfo.id);
              }
            });
          }
        });
      }

      return items;
    }

    const ids = selectedGroup.itemIds;
    const items: MapLocationTypes[] = [];
    ids.forEach((id) => {
      const item = mapLocationEntityIds[id];
      if (item) items.push(item);
    });

    return items;
  }
);

export const getSelectedFocusLocationItem = createSelector<
  RootModel,
  { [key: number]: MapLocationTypes },
  number,
  MapLocationTypes
>(
  (state) => state.mapLocationEntity.ids,
  (state) => state.locationModel.focusItemId,
  (ids, focusItemId) => ids[focusItemId]
);

export const getSelectedLocationItem = createSelector<
  RootModel,
  { [key: number]: MapLocationTypes },
  { key: string; variable: string },
  number,
  LocationItemDetailTypes
>(
  (state) => state.mapLocationEntity.ids,
  (state) => state.locationModel.transport,
  (state) => state.locationModel.itemId,
  (ids, transport, id) => {
    const item = ids[id];
    if (!item) return null;

    const media = item.mapLocationInfo ? item.mapLocationInfo.gallery : null;
    const title = item.mapLocationInfo ? item.mapLocationInfo.name : null;
    const code = item.mapLocationInfo ? item.mapLocationInfo.code : null;
    const description = item.mapLocationInfo ? item.mapLocationInfo.description : null;

    // get sub title base on distance matrix
    let subTitle: string;
    if (item.distanceMatrix && item.distanceMatrix[transport.variable]) {
      const result = getLocationDistanceInfo(item.distanceMatrix[transport.variable]);
      if (result) subTitle = result.distance;
    }

    // get location original data
    const originalData = getLocationOriginalData(item);

    let phone: string;
    if (originalData) phone = originalData.internationalPhoneNumber;

    let website: string;
    if (originalData) website = originalData.website;

    let address: string;
    if (originalData) address = originalData.formattedAddress;

    return { id: item.id, code, media, title, description, subTitle, phone, website, address };
  }
);

export const getSelectedMapLocationItem = createSelector<
  RootModel,
  { [key: number]: MapLocationTypes },
  { key: string; variable: string },
  number,
  number,
  number,
  any
>(
  (state) => state.mapLocationEntity.ids,
  (state) => state.locationModel.transport,
  (state) => state.locationModel.groupId,
  (state) => state.locationModel.subGroupId,
  (state) => state.locationModel.itemId,
  (ids, transport, groupId, subGroupId, itemId) => {
    const item = ids[itemId];
    if (!item) return null;

    const parentId = subGroupId && subGroupId !== -`${groupId}` ? subGroupId : groupId;
    const keyPaths = `${item.id}`.split(`${parentId}`);

    let id: number;
    if (keyPaths.length) id = +keyPaths[0];
    let result: any = {};
    if (item.mapLocationInfo) {
      if (item.mapLocationInfo.location) {
        result = { ...item.mapLocationInfo.location };
      }

      result = {
        ...result,
        id,
        name: item.mapLocationInfo.name,
        category: item.mapLocationInfo.category,
      };
    }
    if (item.distanceMatrix && item.distanceMatrix[transport.variable]) {
      const locationDistanceInfo = getLocationDistanceInfo(item.distanceMatrix[transport.variable]);

      result.transportKey = transport.key;
      result.transportInfo = locationDistanceInfo;
    }

    return result;
  }
);

export const getSelectedFocusMapLocationItem = createSelector<
  RootModel,
  { [key: number]: MapLocationTypes },
  { key: string; variable: string },
  number,
  number,
  number,
  any
>(
  (state) => state.mapLocationEntity.ids,
  (state) => state.locationModel.transport,
  (state) => state.locationModel.groupId,
  (state) => state.locationModel.subGroupId,
  (state) => state.locationModel.focusItemId,
  (ids, transport, groupId, subGroupId, itemId) => {
    const item = ids[itemId];
    if (!item) return null;

    const parentId = subGroupId && subGroupId !== -`${groupId}` ? subGroupId : groupId;
    const keyPaths = `${item.id}`.split(`${parentId}`);

    let id: number;
    if (keyPaths.length) id = +keyPaths[0];
    let result: any = {};
    if (item.mapLocationInfo) {
      if (item.mapLocationInfo.location) {
        result = { ...item.mapLocationInfo.location };
      }

      result = {
        ...result,
        id,
        name: item.mapLocationInfo.name,
        category: item.mapLocationInfo.category,
      };
    }
    if (item.distanceMatrix && item.distanceMatrix[transport.variable]) {
      const locationDistanceInfo = getLocationDistanceInfo(item.distanceMatrix[transport.variable]);

      result.transportKey = transport.key;
      result.transportInfo = locationDistanceInfo;
    }

    return result;
  }
);
